import { useCallback, useMemo } from "react";

import { FormRowField, FormikForm, LandingPageFieldText } from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";
import { moduleSchema } from "@skilitics/nautilus-endpoint-definition";

import { FormBody, Header, Heading, PageContainer, StyledForm, SubHeading } from "../../shared";
import { useDuplicateModuleSettings, type DuplicateModuleSettings } from "../DuplicateModuleSettingsContext";

type FormValues = Pick<DuplicateModuleSettings, "title">;

const validationSchema = moduleSchema.pick({ title: true });

export const ModuleDetailsPage = () => {
  const formatMessage = useFormatMessage(formatStrings);

  const { title, setTitle } = useDuplicateModuleSettings();

  const initialValues: FormValues = useMemo(() => ({ title }), [title]);

  const handleSubmit = useCallback((values: FormValues) => setTitle(values.title), [setTitle]);

  return (
    <PageContainer>
      <Header>
        <Heading>{formatMessage("heading")}</Heading>
        <SubHeading>{formatMessage("subHeading")}</SubHeading>
      </Header>
      <FormikForm<FormValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        submitOnBlur
        formatStrings={formatStrings}
        validationSchema={validationSchema}
        dataTest="module-details-form"
      >
        <StyledForm>
          <FormBody>
            <FormRowField<FormValues> name="title">
              <LandingPageFieldText />
            </FormRowField>
          </FormBody>
        </StyledForm>
      </FormikForm>
    </PageContainer>
  );
};

const formatStrings = {
  heading: {
    en: "Duplicate Training Module",
  },
  subHeading: {
    en: "Module Details",
  },
  titleLabel: {
    en: "What is the title of the duplicated module?",
  },
  titleDescription: {
    en: "The title for the duplicated module. All other module details are copied verbatim and may be updated via the properties panel upon opening the module.",
  },
  titlePlaceholder: {
    en: "Add a title...",
  },
} satisfies FormatMessages;
